import { ErrorHandlingService } from 'src/app/pages/core/services/utils/error-handling.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject, OnDestroy, HostListener } from '@angular/core';
import { CryptoService } from 'src/app/pages/core/abstraction/crypto.service';
import { CryptoConfirmDialogComponent } from '../../../../../../modules/shared/components/dialogs/crypto-confirm-dialog/crypto-confirm-dialog.component';
import { AuthService } from 'src/app/pages/core/abstraction/auth.service';
import { ConfirmDialogComponent } from 'src/app/modules/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ORDER_ERRORS } from 'src/app/pages/core/models/Order';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import BigNumber from 'bignumber.js';
import { GoogleAnalyticsService } from 'src/app/utils/google-analytics.service';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';

// eslint-disable-next-line @typescript-eslint/ban-types
declare let fbq: Function;

@Component({
  selector: 'app-confirm-crypto-order',
  templateUrl: './confirm-crypto-order.component.html',
  styleUrls: ['./confirm-crypto-order.component.scss'],
})
export class ConfirmCryptoOrderComponent implements OnInit, OnDestroy {
  codeTimeout = false;
  priceIntervalRef: any;
  countDownSecond = 10;
  showCountDownSecond: boolean;
  firstLockPriceDone = false;

  isProcessing = false;
  errorText = '';
  showError = false;
  @HostListener('document:keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent) {
    this.handleOrder();
  }

  constructor(
    public dialogRef: MatDialogRef<ConfirmCryptoOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cryptoService: CryptoService,
    private router: Router,
    private errorHandler: ErrorHandlingService,
    private confirmDialog: MatDialog,
    private authService: AuthService,
    private readonly sso: ScrollStrategyOptions,
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.showCountDownSecond = false;
    if (this.data.code) {
      const startTime = new Date().getTime();
      this.setPriceTimer(startTime);
      this.showCountDownSecond = true;
      // Habilitar el botón una vez obtenido el response de primer lock price
      if (this.data.price) {
        this.firstLockPriceDone = true;
      }
    } else {
      this.firstLockPriceDone = true;
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.priceIntervalRef);
  }

  setPriceTimer(startTime: number) {
    this.showCountDownSecond = true;
    this.priceIntervalRef = setInterval(() => {
      const currentTime = new Date().getTime();
      const distance = currentTime - startTime;
      this.countDownSecond = 10 - Math.floor((distance % (1000 * 10)) / 1000);
      if (distance >= 10000) {
        clearInterval(this.priceIntervalRef);
        this.showCountDownSecond = false;
        this.codeTimeout = true;
        this.data.options[0] = 'Refrescar';
      }
    }, 1000);
  }

  relockPrice() {
    this.isProcessing = true;
    const operation = this.data.type ? 'BUY' : 'SELL';
    this.cryptoService.lockMarketOrder(this.data.ticker, operation).subscribe(
      (res: { price: string; code: string }) => {
        if (res) {
          this.codeTimeout = false;
          this.data.options[0] = 'Aceptar';
          const startTime = new Date().getTime();
          this.setPriceTimer(startTime);
          this.data.code = res.code;
          this.data.price = res.price;

          const price = new BigNumber(res.price);
          const currentDestAmount = new BigNumber(this.data.destAmount);
          const currentSrcAmount = new BigNumber(this.data.srcAmount);

          if (operation !== 'BUY') {
            if (this.data.fiatModified) {
              this.data.srcAmount = currentDestAmount.dividedBy(price).toString();
            } else {
              this.data.destAmount = currentSrcAmount.multipliedBy(price).toString();
            }
          } else {
            if (this.data.fiatModified) {
              this.data.destAmount = currentSrcAmount.dividedBy(price).toString();
            } else {
              this.data.srcAmount = currentDestAmount.multipliedBy(price).toString();
            }
          }
          this.isProcessing = false;
        }
      },
      (err) => {
        this.isProcessing = false;
        this.errorText = 'Error en obtener nuevo precio, intentá nuevamente.';
        this.showError = true;
      },
    );
  }

  createOrder(code: string) {
    const operationNumber = this.data.type ? 0 : 1;
    const operationType = this.data.type ? 'BUY' : 'SELL';
    const operationAmount: string = this.data.type ? this.data.destAmount : this.data.srcAmount;
    this.isProcessing = true;
    const newOrder = {
      coin: this.data.symbol, // DAI
      ticker: this.data.ticker, // DAI_USD (if currency is USD)
      operation: operationNumber,
      amount: operationAmount,
      currency: this.data.currency === 0 ? 'USD' : 'ARS',
    };

    this.cryptoService
      .createMarketOrder(newOrder.amount, newOrder.ticker, operationType, code)
      .subscribe(
        (res) => {
          const origAmount = res.amount * res.price;
          this.confirmDialog
            .open(CryptoConfirmDialogComponent, {
              data: {
                ...newOrder,
                origAmount,
              },
              panelClass: 'deposit',
              maxWidth: '90vw',
              scrollStrategy: this.sso.reposition({
                autoClose: false,
                scrollThrottle: 300,
              }),
            })
            .afterClosed()
            .subscribe((res) => {
              this.dialogRef.close(true);
            });
          // Facebook pixel tracking
          fbq(
            'track',
            newOrder.operation === 0 ? `Compra de ${newOrder.coin}` : `Venta de ${newOrder.coin}`,
            {
              currency: newOrder.coin,
              value: newOrder.amount,
            },
          );
          this.authService
            .sendTikTokEvent(
              window.location.href,
              +newOrder.amount,
              this.data.currency === 0 ? 'USD' : 'ARS',
              +newOrder.amount,
              this.data.price,
              newOrder.operation === 0 ? `Compra de ${newOrder.coin}` : `Venta de ${newOrder.coin}`,
            )
            .subscribe();
          this.googleAnalyticsService.emitEvent('market-order', 'first-use', 'click');
          // Google Analytics tracking
          this.router.navigate(['/crypto-exchange'], {
            relativeTo: this.route,
            queryParams: {
              marketTx: true,
              coin: newOrder.coin,
            },
          });
          this.isProcessing = false;
          this.dialogRef.close(true);
        },
        (error) => {
          this.isProcessing = false;
          const errorMessages = this.errorHandler.handleError(error);
          const ref = this.confirmDialog.open<AlertDialogComponent, AlertDialogData>(
            AlertDialogComponent,
            {
              panelClass: 'modal-container',
              data: {
                title: errorMessages.title,
                content: errorMessages.body,
                errorCode: errorMessages.errorCode,
                isError: true,
              },
            },
          );
          ref.afterClosed().subscribe((data) => {
            if (data) {
              if (error.error.internalStatus === ORDER_ERRORS.BLOCKED_UIF) {
                this.dialogRef.close();
                this.router.navigate(['profile', 'documentation'], {
                  state: { toDocumentation: true },
                });
              }
            }
            if (error.error.internalStatus === ORDER_ERRORS.NEEDS_EXTRA_BANK_ACCOUNT) {
              this.dialogRef.close();
              this.router.navigate(['profile', 'accounts']);
            }
          });
        },
      );
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  handleOrder() {
    if (this.codeTimeout) {
      this.relockPrice();
    } else {
      this.createOrder(this.data.code);
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
